import React, { useState, useEffect } from 'react';
import { performanceMonitor } from '../../utils/performanceMonitor';
import { routePreloader } from '../../utils/routePreloader';
import { featureLoader } from '../../utils/dynamicFeatureLoader';
import { BundleAnalyzer } from '../../utils/bundleOptimization';
import './BundlePerformanceMonitor.css';

interface BundlePerformanceMonitorProps {
  isVisible: boolean;
  onClose: () => void;
}

const BundlePerformanceMonitor: React.FC<BundlePerformanceMonitorProps> = ({
  isVisible,
  onClose
}) => {
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [routeStats, setRouteStats] = useState<any>(null);
  const [bundleInfo, setBundleInfo] = useState<any>(null);
  const [bundleReport, setBundleReport] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'performance' | 'routes' | 'bundles' | 'features'>('performance');

  useEffect(() => {
    if (isVisible) {
      updateData();
      const interval = setInterval(updateData, 2000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const updateData = () => {
    setPerformanceData(performanceMonitor.getPerformanceSummary());
    setRouteStats(routePreloader.getStats());
    setBundleInfo(featureLoader.getBundleInfo());
    setBundleReport(BundleAnalyzer.getReport());
  };

  const exportData = () => {
    const data = {
      performance: performanceData,
      routes: routeStats,
      bundles: bundleInfo,
      bundleAnalysis: bundleReport,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bundle-performance-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearData = () => {
    performanceMonitor.clearMetrics();
    updateData();
  };

  if (!isVisible) return null;

  return (
    <div className="bundle-performance-monitor">
      <div className="monitor-header">
        <h3>Bundle Performance Monitor</h3>
        <div className="monitor-actions">
          <button onClick={exportData} className="export-btn">Export Data</button>
          <button onClick={clearData} className="clear-btn">Clear</button>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
      </div>

      <div className="monitor-tabs">
        <button 
          className={activeTab === 'performance' ? 'active' : ''}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
        <button 
          className={activeTab === 'routes' ? 'active' : ''}
          onClick={() => setActiveTab('routes')}
        >
          Routes
        </button>
        <button 
          className={activeTab === 'bundles' ? 'active' : ''}
          onClick={() => setActiveTab('bundles')}
        >
          Bundles
        </button>
        <button 
          className={activeTab === 'features' ? 'active' : ''}
          onClick={() => setActiveTab('features')}
        >
          Features
        </button>
      </div>

      <div className="monitor-content">
        {activeTab === 'performance' && performanceData && (
          <div className="performance-tab">
            <div className="metrics-grid">
              <div className="metric-card">
                <h4>Core Web Vitals</h4>
                <div className="vitals">
                  {Object.entries(performanceData.summary.coreWebVitals).map(([key, value]) => (
                    <div key={key} className="vital">
                      <span className="vital-name">{key}</span>
                      <span className="vital-value">{(value as number).toFixed(2)}ms</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="metric-card">
                <h4>Chunk Loading</h4>
                <div className="chunk-stats">
                  <div className="stat">
                    <span>Average Load Time</span>
                    <span>{performanceData.summary.averageChunkLoadTime.toFixed(2)}ms</span>
                  </div>
                  <div className="stat">
                    <span>Total Chunks</span>
                    <span>{performanceData.summary.totalChunksLoaded}</span>
                  </div>
                  <div className="stat">
                    <span>Cached Chunks</span>
                    <span>{performanceData.summary.cachedChunks}</span>
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <h4>Route Changes</h4>
                <div className="route-stats">
                  <div className="stat">
                    <span>Average Time</span>
                    <span>{performanceData.summary.averageRouteChangeTime.toFixed(2)}ms</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="recent-metrics">
              <h4>Recent Metrics</h4>
              <div className="metrics-list">
                {performanceData.metrics.slice(-10).map((metric: any, index: number) => (
                  <div key={index} className="metric-item">
                    <span className="metric-name">{metric.name}</span>
                    <span className="metric-value">{metric.value.toFixed(2)}ms</span>
                    <span className="metric-time">
                      {new Date(metric.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'routes' && routeStats && (
          <div className="routes-tab">
            <div className="route-summary">
              <div className="summary-card">
                <h4>Route Statistics</h4>
                <div className="stats">
                  <div className="stat">
                    <span>Total Routes</span>
                    <span>{routeStats.totalRoutes}</span>
                  </div>
                  <div className="stat">
                    <span>Preloaded</span>
                    <span>{routeStats.preloadedRoutes}</span>
                  </div>
                  <div className="stat">
                    <span>Loading</span>
                    <span>{routeStats.preloadingRoutes}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="preloaded-routes">
              <h4>Preloaded Routes</h4>
              <div className="routes-list">
                {routeStats.preloadedPaths.map((path: string) => (
                  <div key={path} className="route-item">
                    <span className="route-path">{path}</span>
                    <span className="route-status">✓ Loaded</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bundles' && bundleReport && (
          <div className="bundles-tab">
            <div className="bundle-summary">
              <div className="summary-card">
                <h4>Bundle Analysis</h4>
                <div className="stats">
                  <div className="stat">
                    <span>Total Chunks</span>
                    <span>{bundleReport.totalChunks}</span>
                  </div>
                  <div className="stat">
                    <span>Total Size</span>
                    <span>{(bundleReport.totalSize / 1024).toFixed(2)} KB</span>
                  </div>
                  <div className="stat">
                    <span>Avg Load Time</span>
                    <span>{bundleReport.averageLoadTime.toFixed(2)}ms</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="chunks-list">
              <h4>Loaded Chunks</h4>
              <div className="chunks">
                {bundleReport.chunks.map((chunk: any, index: number) => (
                  <div key={index} className="chunk-item">
                    <span className="chunk-name">{chunk.name}</span>
                    {chunk.size && (
                      <span className="chunk-size">{(chunk.size / 1024).toFixed(2)} KB</span>
                    )}
                    {chunk.loadTime && (
                      <span className="chunk-time">{chunk.loadTime.toFixed(2)}ms</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'features' && bundleInfo && (
          <div className="features-tab">
            <div className="feature-summary">
              <div className="summary-card">
                <h4>Dynamic Features</h4>
                <div className="stats">
                  <div className="stat">
                    <span>Registered</span>
                    <span>{bundleInfo.registered.length}</span>
                  </div>
                  <div className="stat">
                    <span>Loaded</span>
                    <span>{bundleInfo.loaded.length}</span>
                  </div>
                  <div className="stat">
                    <span>Loading</span>
                    <span>{bundleInfo.loading.length}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="features-lists">
              <div className="feature-list">
                <h4>Loaded Features</h4>
                {bundleInfo.loaded.map((feature: string) => (
                  <div key={feature} className="feature-item loaded">
                    <span>{feature}</span>
                    <span className="status">✓</span>
                  </div>
                ))}
              </div>

              <div className="feature-list">
                <h4>Loading Features</h4>
                {bundleInfo.loading.map((feature: string) => (
                  <div key={feature} className="feature-item loading">
                    <span>{feature}</span>
                    <span className="status">⏳</span>
                  </div>
                ))}
              </div>

              <div className="feature-list">
                <h4>Registered Features</h4>
                {bundleInfo.registered.map((feature: string) => (
                  <div key={feature} className="feature-item registered">
                    <span>{feature}</span>
                    <span className="status">○</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BundlePerformanceMonitor;